import {
  fractionHanzi,
  numeralHanzi,
  percentHanzi,
  readNumeralHanzi,
  readWord,
  type Syllable,
} from "@kensio/pinyinjs";

import { financialNumerals, financialPinyin } from "./financial-numerals.js";
import { writePinyinWord } from "./syllable-text.js";

/**
 * Numbers, said in Chinese.
 *
 * What makes this worth a page of its own is that the same digits go two
 * different ways depending on what they are counting, and nothing about the
 * number itself says which. 2026年 is `èr líng èr liù nián`, read out one
 * digit at a time, and 2026个 is `liǎng qiān líng èr shí liù gè`, counted. A
 * learner who has only met one of those will read a year like a quantity, and
 * a phone number like an astronomical sum.
 *
 * So this shows both rather than guessing which was meant.
 */

/** One way of saying a number: the characters, and how they are read. */
export interface NumeralReading {
  readonly hanzi: string;
  readonly pinyin: string;
}

/**
 * The words that are not numerals but appear among them.
 *
 * 百分之 and 分之 are read from pinyin rather than from their characters,
 * because `readNumeralHanzi` knows the numeral characters and 分 and 之 are
 * not among them. Reading a spelling this package can already parse is a
 * smaller thing to write down than three hand-built syllables.
 *
 * Both are literals, so both read. A fallback here would be a fallback for the
 * package having stopped reading pinyin at all.
 */
const percentPrefix = readWord("bǎifēnzhī")!;
const fractionInfix = readWord("fēnzhī")!;

/**
 * Write a run of syllables as tone-marked pinyin, one word per syllable.
 *
 * Spaced rather than run together, which is what the package's own conversion
 * does with a number it has read: 2026个 comes out as
 * `liǎng qiān líng èr shí liù gè`. Solid would also be defensible under
 * GB/T 16159, but `bǎifēnzhīliǎngqiānlíngèrshíliù` is not something anybody
 * should have to read off a page they came to for help.
 */
function said(syllables: readonly Syllable[]): string {
  return syllables
    .map((syllable) => writePinyinWord([syllable], "marks"))
    .join(" ");
}

/**
 * Read a run of numeral characters, or nothing if it holds anything else.
 */
function readNumerals(
  hanzi: string,
  isYao = false,
): readonly Syllable[] | undefined {
  return readNumeralHanzi(hanzi, { yao: isYao });
}

/** A numeral and its reading, where the numeral could be read at all. */
function reading(
  hanzi: string | undefined,
  syllables: readonly Syllable[] | undefined,
): NumeralReading | undefined {
  return hanzi === undefined || syllables === undefined
    ? undefined
    : { hanzi, pinyin: said(syllables) };
}

/** Everything the page shows about one number. */
export interface NumberReadings {
  /** Counted, as a quantity: 2026 → 两千零二十六. */
  readonly cardinal: NumeralReading | undefined;
  /** Read out digit by digit, as a year is: 二〇二六. */
  readonly digits: NumeralReading | undefined;
  /**
   * The digits with 一 said `yāo`, as a phone or room number takes it.
   *
   * Absent where it would say the same thing as {@link digits}, which is every
   * number with no 1 in it — showing an identical third row would suggest a
   * distinction that is not there.
   */
  readonly yao: NumeralReading | undefined;
  /** As a percentage: 95 → 百分之九十五. */
  readonly percent: NumeralReading | undefined;
  /**
   * As an amount of money in financial numerals: 1409.50 → 壹仟肆佰零玖元伍角.
   *
   * Absent for a negative number and for anything finer than a 分, neither of
   * which a cheque can be written for.
   */
  readonly financial: NumeralReading | undefined;
}

/**
 * The ways of saying a number, or all-undefined for something that is not one.
 *
 * Nothing here throws on bad input, because the input is a field somebody is
 * still typing in: a lone `-` on the way to `-5` is not an error worth
 * reporting, it is a number that is not finished yet.
 */
export function readNumber(value: string): NumberReadings {
  const trimmed = value.trim();

  const cardinalHanzi = numeralHanzi(trimmed);
  const digitsHanzi = numeralHanzi(trimmed, { style: "digits" });

  const cardinal = reading(
    cardinalHanzi,
    cardinalHanzi === undefined ? undefined : readNumerals(cardinalHanzi),
  );
  const digits = reading(
    digitsHanzi,
    digitsHanzi === undefined ? undefined : readNumerals(digitsHanzi),
  );
  const yao = reading(
    digitsHanzi,
    digitsHanzi === undefined ? undefined : readNumerals(digitsHanzi, true),
  );

  // A row is dropped where it would say the same thing as the row above it. A
  // decimal is read the same way whether counted or spelled out, and a number
  // with no 1 in it has no `yāo` reading distinct from its ordinary one —
  // repeating either would imply a distinction that is not there.
  return {
    cardinal,
    digits: digits?.pinyin === cardinal?.pinyin ? undefined : digits,
    yao: yao?.pinyin === digits?.pinyin ? undefined : yao,
    // 百分之负二十五 is not how anyone says −25%; that is 负百分之二十五, with
    // the sign outside. Rather than assemble a form the package does not
    // produce, a negative number simply has no percentage row.
    percent: trimmed.startsWith("-")
      ? undefined
      : percentReading(percentHanzi(trimmed)),
    financial: financialReading(financialNumerals(trimmed)),
  };
}

/**
 * A percentage: 百分之 followed by the number, counted.
 */
function percentReading(hanzi: string | undefined): NumeralReading | undefined {
  if (hanzi === undefined) {
    return undefined;
  }

  const number = readNumerals(hanzi.replace("百分之", ""));

  return number === undefined
    ? undefined
    : { hanzi, pinyin: said([...percentPrefix, ...number]) };
}

/**
 * An amount in financial numerals, read as the everyday numerals they stand
 * for.
 */
function financialReading(
  hanzi: string | undefined,
): NumeralReading | undefined {
  return hanzi === undefined
    ? undefined
    : { hanzi, pinyin: financialPinyin(hanzi) };
}

/**
 * A fraction, said the way Chinese says it: the denominator first.
 *
 * 3/4 is 四分之三 — "of four parts, three" — which reverses the order English
 * uses, and is the thing about Chinese fractions that learners most reliably
 * get backwards.
 */
export function readFraction(
  numerator: string,
  denominator: string,
): NumeralReading | undefined {
  const hanzi = fractionHanzi(numerator.trim(), denominator.trim());
  if (hanzi === undefined) {
    return undefined;
  }

  const [bottom = "", top = ""] = hanzi.split("分之");
  const bottomSaid = readNumerals(bottom);
  const topSaid = readNumerals(top);

  return bottomSaid === undefined || topSaid === undefined
    ? undefined
    : {
        hanzi,
        pinyin: said([...bottomSaid, ...fractionInfix, ...topSaid]),
      };
}
